'use client';

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  edgeLambdaKeys,
  fetchThreatReportCreators,
  type ThreatReportCreator,
} from '@/shared/cors-edge-lambda-api';
import { useCellServiceSession } from './useCellServiceSession';

/**
 * Accounts ("selectors") found in one Fivecast export, for the Create New
 * Threat Report modal's creator grid.
 *
 * Stays disabled until an export is picked, which is what keeps the modal's
 * default state grid-free: no source means no request and no rows.
 *
 * A case's exports are immutable once processed, so the result is cached for
 * the session's lifetime — reopening the modal or flipping between exports
 * re-renders from cache instead of refetching.
 */
export function useThreatReportCreators(
  caseId: string | undefined | null,
  source: string | undefined | null,
): UseQueryResult<ThreatReportCreator[], Error> {
  const { cellId, accessToken, enabled } = useCellServiceSession();
  const trimmedCaseId = caseId?.trim() || '';

  return useQuery<ThreatReportCreator[], Error>({
    queryKey: edgeLambdaKeys.threatReportCreators(cellId, trimmedCaseId, source ?? null),
    queryFn: ({ signal }) =>
      fetchThreatReportCreators({
        cellId: cellId as string,
        accessToken: accessToken as string,
        caseId: trimmedCaseId,
        source: source as string,
        signal,
      }),
    enabled: enabled && Boolean(trimmedCaseId) && Boolean(source),
    // Exports are immutable once processed, but failed lookups must retry within
    // the session — do not treat errors as permanently fresh.
    staleTime: Infinity,
    retry: 2,
  });
}
